/* eslint-disable complexity */
import { GridCol, GridContainer, GridRow, Icon, ICONS } from '@bob/library'
import { PageContext } from '@websrc/App/AppContext'
import { AppProvider } from '@websrc/App/AppProvider'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { ProductResourceHeader } from '@websrc/App/Components/ProductResourceHeader/ProductResourceHeader'
import { ProductResourceNav } from '@websrc/App/Components/ProductResourceNav/ProductResourceNav'
import { pathToPageName } from '@websrc/App/Helpers/pathToPageName'
import { BuildingInstruction } from '@websrc/App/Pages/ProductResourcePage/BuildingInstruction/BuildingInstruction'
import { SupportPDF } from '@websrc/App/Pages/ProductResourcePage/SupportPDF/SupportPDF'
import {
  isMappedAsset,
  isMappedBuildingInstruction,
  isMappedCtaItem,
  isMappedSupportPDF,
  isMappedText,
} from '@websrc/DataMappers/Guards/resourcePageSectionItemGuards'
import { mapProductResourcesPage } from '@websrc/DataMappers/mapProductResourcePage'
import { mapSubSection } from '@websrc/DataMappers/mapResourcePageSection'
import React, { Fragment } from 'react'

import * as SC from './ProductResourcePage.style'

export interface PageRenderProps {
  page: ReturnType<typeof mapProductResourcesPage>
  content: ReturnType<typeof mapSubSection>
}

export interface ProductResourcePageProps {
  pageContext: PageContext & PageRenderProps
}

export function ProductResourcePageRender({
  page: { asset, caption, headline, link, sections },
  content,
}: PageRenderProps) {
  return (
    <GridContainer>
      <ProductResourceHeader asset={asset} caption={caption} headline={headline} link={link} />
      <GridRow>
        <GridCol>
          <SC.StyledContentGridRow>
            <SC.StyledNavGridCol tablet={4}>
              <ProductResourceNav sections={sections} />
            </SC.StyledNavGridCol>

            <SC.StyledContentGridCol tablet={8}>
              {content?.subsection?.content && (
                <GridCol>
                  <Markdown headerIds={false} markdown={content.subsection.content} />
                </GridCol>
              )}

              {content.subsection.items.map((i, contentIndex) => {
                const subSectionCaptionId = isMappedText(i) && i.text.captionId

                return (
                  <Fragment key={contentIndex}>
                    {subSectionCaptionId && (
                      <>
                        <span id={subSectionCaptionId} />
                        {contentIndex > 0 && <SC.StyledHR />}
                      </>
                    )}
                    {isMappedAsset(i) && i.asset && (
                      <GridCol>
                        <Asset
                          {...i.asset}
                          disable={i.asset.video ? '' : 'upscale'}
                          lazyLoading={false}
                          sizes={'(min-width: 1024px) 600px'}
                          srcSetWidths={[320, 400, 600, 800, 1200]}
                        />
                      </GridCol>
                    )}
                    {isMappedText(i) && i.text.markdown && (
                      <GridCol>
                        <Markdown headerIds={false} markdown={i.text.markdown} />
                      </GridCol>
                    )}
                    {isMappedBuildingInstruction(i) && i?.buildingInstruction && (
                      <BuildingInstruction bi={i.buildingInstruction} />
                    )}
                    {isMappedSupportPDF(i) && i?.pdf && <SupportPDF supportPDF={i.pdf} />}
                    {isMappedCtaItem(i) && i?.cta && i.cta.cta_text && (
                      <GridCol>
                        <SC.StyledCTA ctaType={'tertiary'} link={i.cta}>
                          <Icon icon={ICONS.ARROW_RIGHT} size={16} />
                          <span>{i.cta.cta_text}</span>
                        </SC.StyledCTA>
                      </GridCol>
                    )}
                  </Fragment>
                )
              })}
            </SC.StyledContentGridCol>
          </SC.StyledContentGridRow>
        </GridCol>
      </GridRow>
    </GridContainer>
  )
}

export default function ProductResourcePage({ pageContext }: ProductResourcePageProps) {
  const { page, content, appContext } = pageContext
  const [siteSection, pageName] = pathToPageName(appContext.path)
  const pageType = 'product-resources'

  return (
    <AppProvider {...appContext} pageName={pageName} pageType={pageType} siteSection={siteSection}>
      <ProductResourcePageRender content={content} page={page} />
    </AppProvider>
  )
}
